import PropTypes from 'prop-types'
import React, { useRef, useState } from 'react'
import ReactCrop, {
    centerCrop,
    makeAspectCrop
    // convertToPixelCrop
} from 'react-image-crop'

import 'react-image-crop/dist/ReactCrop.css'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import CloseIcon from '@mui/icons-material/Close'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import { Box, Button, Grid, IconButton, Input } from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import { MuiFileInput } from 'mui-file-input'
import DownloadIcon from '@/icons/Download'
import { canvasPreview } from './canvasPreview'
import { useDebounceEffect } from './useDebounceEffect'

// This is to demonstate how to make and center a % aspect crop
// which is a bit trickier so we use some helper functions.
function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
    return centerCrop(
        makeAspectCrop(
            {
                unit: '%',
                width: 90
            },
            aspect,
            mediaWidth,
            mediaHeight
        ),
        mediaWidth,
        mediaHeight
    )
}

const ImageCrop = ({ setImageSelected, setCompleteImage }) => {
    const [originalImgSrc, setOriginalImgSrc] = useState(null) // only for the file input to show the file name

    const [imgSrc, setImgSrc] = useState(null)
    const previewCanvasRef = useRef(null)
    const imgRef = useRef(null)
    const hiddenAnchorRef = useRef(null)
    const blobUrlRef = useRef('')
    const [crop, setCrop] = useState()
    const [completedCrop, setCompletedCrop] = useState()
    const [scale, setScale] = useState(1)
    const [rotate, setRotate] = useState(0)
    const [aspect, setAspect] = useState(8 / 8)

    const changeScale = (inOrOut) => {
        if (inOrOut === 'out') {
            setScale((prev) => Math.max(prev - 0.1, 0.1))
        } else {
            setScale((prev) => Math.max(prev + 0.1, 0.1))
        }
    }

    const changeRotate = (leftOrRight) => {
        if (leftOrRight === 'right') {
            setRotate((prev) => Math.min(180, prev + 5))
        } else {
            setRotate((prev) => Math.max(-180, prev - 5))
        }
    }

    // for plain file input
    // function onSelectFile(e) {
    //     if (e.target.files && e.target.files.length > 0) {
    //         setCrop(undefined) // Makes crop preview update between images.
    //         const reader = new FileReader()
    //         reader.addEventListener('load', () => {
    //             setImgSrc(reader.result?.toString() || '')
    //
    //         })
    //         reader.readAsDataURL(e.target.files[0])
    //     }
    // }

    // for mui file input
    function onSelectFile(file) {
        if (file) {
            setOriginalImgSrc(file) // only for the file input to show the file name
            setCrop(undefined) // Makes crop preview update between images.
            const reader = new FileReader()
            reader.addEventListener('load', () => {
                setImgSrc(reader.result?.toString() || '')
            })
            reader.readAsDataURL(file)
        }
    }

    function onImageLoad(e) {
        if (aspect) {
            const { width, height } = e.currentTarget
            setCrop(centerAspectCrop(width, height, aspect))
            setImageSelected(true)
        }
    }

    async function onDownloadCropClick() {
        const image = imgRef.current
        const previewCanvas = previewCanvasRef.current
        if (!image || !previewCanvas || !completedCrop) {
            throw new Error('Crop canvas does not exist')
        }

        // This will size relative to the uploaded image
        // size. If you want to size according to what they
        // are looking at on screen, remove scaleX + scaleY
        const scaleX = image.naturalWidth / image.width
        const scaleY = image.naturalHeight / image.height

        const offscreen = new OffscreenCanvas(
            completedCrop.width * scaleX,
            completedCrop.height * scaleY
        )
        const ctx = offscreen.getContext('2d')
        if (!ctx) {
            throw new Error('No 2d context')
        }

        ctx.drawImage(
            previewCanvas,
            0,
            0,
            previewCanvas.width,
            previewCanvas.height,
            0,
            0,
            offscreen.width,
            offscreen.height
        )
        // You might want { type: "image/jpeg", quality: <0 to 1> } to
        // reduce image size
        const blob = await offscreen.convertToBlob({
            type: 'image/png'
        })

        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current)
        }
        blobUrlRef.current = URL.createObjectURL(blob)

        if (hiddenAnchorRef.current) {
            hiddenAnchorRef.current.href = blobUrlRef.current
            hiddenAnchorRef.current.click()
        }
    }

    useDebounceEffect(
        async () => {
            if (
                completedCrop?.width &&
                completedCrop?.height &&
                imgRef.current
            ) {
                if (completedCrop) {
                    // create a canvas element to draw the cropped image
                    const canvas = document.createElement('canvas')

                    // get the image element
                    const image = imgRef.current

                    // draw the image on the canvas
                    if (image) {
                        const crop = completedCrop
                        const scaleX = image.naturalWidth / image.width
                        const scaleY = image.naturalHeight / image.height
                        const ctx = canvas.getContext('2d')
                        const pixelRatio = window.devicePixelRatio
                        canvas.width = crop.width * pixelRatio * scaleX
                        canvas.height = crop.height * pixelRatio * scaleY

                        if (ctx) {
                            ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
                            ctx.imageSmoothingQuality = 'high'

                            ctx.drawImage(
                                image,
                                crop.x * scaleX,
                                crop.y * scaleY,
                                crop.width * scaleX,
                                crop.height * scaleY,
                                0,
                                0,
                                crop.width * scaleX,
                                crop.height * scaleY
                            )
                        }

                        const base64Image = canvas.toDataURL('image/png') // can be changed to jpeg/jpg etc

                        if (base64Image) {
                            const fileType = base64Image
                                .split(';')[0]
                                .split(':')[1]

                            const base64Data = base64Image.replace(
                                /^data:image\/\w+;base64,/,
                                ''
                            )
                            const binaryString = atob(base64Data)
                            const bytes = new Uint8Array(binaryString.length)
                            for (let i = 0; i < binaryString.length; i++) {
                                bytes[i] = binaryString.charCodeAt(i)
                            }
                            const file = new File([bytes], 'avatar.png', {
                                type: fileType
                            })
                            setCompleteImage(file) // function passed as a prop
                        }
                    }
                }
            }

            // // this is for the image preview that we're not currently using, but keep it anyway
            if (
                completedCrop?.width &&
                completedCrop?.height &&
                imgRef.current &&
                previewCanvasRef.current
            ) {
                // We use canvasPreview as it's much faster than imgPreview.
                canvasPreview(
                    imgRef.current,
                    previewCanvasRef.current,
                    completedCrop,
                    scale,
                    rotate
                )
            }
        },
        100,
        [completedCrop, scale, rotate]
    )

    // function handleToggleAspectClick() {
    //     if (aspect) {
    //         setAspect(undefined)
    //     } else {
    //         setAspect(16 / 9)
    //
    //         if (imgRef.current) {
    //             const { width, height } = imgRef.current
    //             const newCrop = centerAspectCrop(width, height, 16 / 9)
    //             setCrop(newCrop)
    //             // Updates the preview
    //             setCompletedCrop(convertToPixelCrop(newCrop, width, height))
    //         }
    //     }
    // }

    return (
        <Grid container>
            <Grid sx={{ mb: 2 }} size={12}>
                {/* <Input */}
                {/*    id="image" */}
                {/*    name="image" */}
                {/*    type="file" */}
                {/*    onChange={(e) => { */}
                {/*        onSelectFile(e) */}
                {/*    }} */}
                {/* /> */}

                <MuiFileInput // to avoid this faff... https://mui.com/material-ui/react-button/#file-upload
                    id="image"
                    fullWidth
                    placeholder="Choose an image"
                    InputProps={{
                        inputProps: {
                            accept: 'image/*'
                        },
                        startAdornment: (
                            <InputAdornment position="start" sx={{ mb: 2 }}>
                                <AttachFileIcon />
                            </InputAdornment>
                        )
                    }}
                    onChange={(file) => {
                        onSelectFile(file)
                    }}
                    required
                    value={originalImgSrc}
                />
            </Grid>
            {imgSrc && (
                <>
                    <Grid
                        size={{
                            xs: 3,
                            sm: 2
                        }}
                    >
                        <Button
                            type="button"
                            sx={{ m: 1 }}
                            variant="contained"
                            onClick={() => changeScale('in')}
                        >
                            <ZoomInIcon />
                        </Button>
                    </Grid>
                    <Grid
                        size={{
                            xs: 3,
                            sm: 2
                        }}
                    >
                        <Button
                            type="button"
                            sx={{ m: 1 }}
                            variant="contained"
                            onClick={() => changeScale('out')}
                        >
                            <ZoomOutIcon />
                        </Button>
                    </Grid>
                    <Grid
                        size={{
                            xs: 3,
                            sm: 2
                        }}
                    >
                        <Button
                            type="button"
                            sx={{ m: 1 }}
                            variant="contained"
                            onClick={() => changeRotate('left')}
                        >
                            <RotateLeftIcon />
                        </Button>
                    </Grid>
                    <Grid
                        size={{
                            xs: 3,
                            sm: 2
                        }}
                    >
                        <Button
                            type="button"
                            sx={{ m: 1 }}
                            variant="contained"
                            onClick={() => changeRotate('right')}
                        >
                            <RotateRightIcon />
                        </Button>
                    </Grid>
                </>
            )}
            {/* <div> */}
            {/*    <button onClick={handleToggleAspectClick}> */}
            {/*        Toggle aspect {aspect ? 'off' : 'on'} */}
            {/*    </button> */}
            {/* </div> */}
            {!!imgSrc && (
                <Grid size={12}>
                    <ReactCrop
                        crop={crop}
                        onChange={(_, percentCrop) => setCrop(percentCrop)}
                        onComplete={(c) => setCompletedCrop(c)}
                        aspect={aspect}
                        // minWidth={400}
                        minHeight={100}
                        // circularCrop
                    >
                        <img
                            ref={imgRef}
                            alt="Crop me"
                            src={imgSrc}
                            style={{
                                transform: `scale(${scale}) rotate(${rotate}deg)`
                            }}
                            onLoad={onImageLoad}
                        />
                    </ReactCrop>
                </Grid>
            )}
            {!!completedCrop && (
                <>
                    <Box display="flex" flexDirection="row">
                        <canvas
                            ref={previewCanvasRef}
                            style={{
                                objectFit: 'contain',
                                width: 150,
                                minHeight: 150
                                // width    : completedCrop.width,
                                // height   : completedCrop.height
                            }}
                        />

                        <div>
                            <IconButton
                                type="button"
                                title="Download Profile Picture"
                                onClick={() => onDownloadCropClick()}
                            >
                                <DownloadIcon fontSize="small" />
                            </IconButton>
                            <a
                                href="#hidden"
                                ref={hiddenAnchorRef}
                                download
                                style={{
                                    position: 'absolute',
                                    top: '-200vh',
                                    visibility: 'hidden'
                                }}
                            >
                                Hidden download
                            </a>
                        </div>
                    </Box>
                </>
            )}
        </Grid>
    )
}

ImageCrop.propTypes = {
    setImageSelected: PropTypes.func,
    setCompleteImage: PropTypes.func
}

export default ImageCrop
